import React, { useEffect, useMemo, useState } from 'react';
import { useRoleViewFetch } from './RoleViewContext';
import './NotarialAgenda.css';

const API = process.env.REACT_APP_API_URL || 'http://localhost:5001';
const TIME_ZONE = 'America/Monterrey';
const localDay = date => new Intl.DateTimeFormat('sv-SE', { timeZone: TIME_ZONE, year: 'numeric', month: '2-digit', day: '2-digit' }).format(new Date(date));
const getMonth = () => new Intl.DateTimeFormat('sv-SE', { timeZone: TIME_ZONE, year: 'numeric', month: '2-digit' }).format(new Date());
const monthLabel = value => {
  const [year, month] = value.split('-').map(Number);
  return new Intl.DateTimeFormat('es-MX', { month: 'long', year: 'numeric', timeZone: 'UTC' }).format(new Date(Date.UTC(year, month - 1, 10)));
};
const timeLabel = value => new Date(value).toLocaleTimeString('es-MX', { timeZone: TIME_ZONE, hour: '2-digit', minute: '2-digit' });

export default function NotarialCalendar({ onOpen }) {
  const apiFetch = useRoleViewFetch();
  const [month, setMonth] = useState(getMonth);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedDay, setSelectedDay] = useState(null);

  useEffect(() => {
    if (!/^\d{4}-(0[1-9]|1[0-2])$/.test(month)) return;
    const controller = new AbortController();
    let alive = true;
    setError(''); setLoading(true); setSelectedDay(null);
    apiFetch(`${API}/api/notario-dashboard/appointments?month=${month}`, {
      headers: { 'x-auth-token': localStorage.getItem('token') || sessionStorage.getItem('token') },
      signal: controller.signal,
    }).then(async response => {
      const data = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(data.msg || 'No se pudieron consultar las citas.');
      return data.items;
    }).then(data => { if (alive) setItems(Array.isArray(data) ? data : []); })
      .catch(err => { if (alive && err.name !== 'AbortError') setError(err.message || 'Error de conexión.'); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; controller.abort(); };
  }, [month, apiFetch]);

  const changeMonth = delta => {
    const [year, number] = month.split('-').map(Number);
    const result = new Date(Date.UTC(year, number - 1 + delta, 1));
    setMonth(`${result.getUTCFullYear()}-${String(result.getUTCMonth() + 1).padStart(2, '0')}`);
  };

  const today = localDay(new Date());
  const appointments = useMemo(() => items.filter(item => item.startsAt && !Number.isNaN(new Date(item.startsAt).getTime())).sort((a, b) => new Date(a.startsAt) - new Date(b.startsAt)), [items]);
  const dates = useMemo(() => {
    const [year, number] = month.split('-').map(Number);
    const count = new Date(Date.UTC(year, number, 0)).getUTCDate();
    const padding = (new Date(Date.UTC(year, number - 1, 1)).getUTCDay() + 6) % 7;
    const cells = Array.from({ length: padding }, (_, i) => ({ key: `blank-${i}`, blank: true }));
    for (let day = 1; day <= count; day++) {
      const iso = `${month}-${String(day).padStart(2, '0')}`;
      cells.push({ key: iso, iso, day });
    }
    const trailing = (7 - cells.length % 7) % 7;
    for (let i = 0; i < trailing; i++) cells.push({ key: `trailing-${i}`, blank: true });
    return cells;
  }, [month]);
  const byDate = useMemo(() => {
    const map = new Map();
    for (const appointment of appointments) {
      const day = localDay(appointment.startsAt);
      if (!map.has(day)) map.set(day, []);
      map.get(day).push(appointment);
    }
    return map;
  }, [appointments]);
  const selected = selectedDay ? (byDate.get(selectedDay) || []) : [];
  const open = appointment => { if (appointment?.poderId && onOpen) onOpen({ _id: appointment.poderId }); };

  return <section className="notarial-agenda" aria-label="Agenda notarial">
    <header className="na-header">
      <div className="na-heading">
        <span className="na-eyebrow">WISBY · NOTARÍA</span>
        <h2>Calendario de firmas físicas</h2>
        <p>Consulta las citas de tu notaría en horario de Monterrey y abre los expedientes correspondientes.</p>
      </div>
      <div className="na-month-actions">
        <button type="button" className="na-control" onClick={() => changeMonth(-1)} aria-label="Mes anterior">‹</button>
        <label className="na-month-picker"><span className="sr-only">Seleccionar mes</span><input aria-label="Seleccionar mes" type="month" value={month} onChange={e => { if (/^\d{4}-(0[1-9]|1[0-2])$/.test(e.target.value)) setMonth(e.target.value); }}/></label>
        <button type="button" className="na-control" onClick={() => changeMonth(1)} aria-label="Mes siguiente">›</button>
        <button type="button" className="na-today" onClick={() => { setMonth(getMonth()); setSelectedDay(today); }}>Hoy</button>
      </div>
    </header>
    <div className="na-summary" aria-live="polite">
      <div><span className="na-count">{appointments.length}</span><span>Citas programadas</span></div>
      <div><span className="na-count">{new Set(appointments.map(p => localDay(p.startsAt))).size}</span><span>Días con actividad</span></div>
      <div><span className="na-zone-dot" /> Horario de Monterrey</div>
    </div>
    {error && <p className="na-error" role="alert">{error}</p>}
    {loading && <p className="na-loading" role="status">Cargando citas de {monthLabel(month)}…</p>}
    <div className="na-calendar-scroll">
      <div className="na-calendar" aria-label={`Calendario de ${monthLabel(month)}`}>
        {['Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb', 'Dom'].map(day => <div className="na-weekday" key={day}>{day}</div>)}
        {dates.map(cell => cell.blank ? <div key={cell.key} className="na-cell na-cell--outside" aria-hidden="true" /> : (
          <div key={cell.key} className={`na-cell${cell.iso === today ? ' na-cell--today' : ''}${selectedDay === cell.iso ? ' na-cell--selected' : ''}`}>
            <button type="button" className="na-day-select" aria-label={`Ver citas del ${cell.day} de ${monthLabel(month)}`} onClick={() => setSelectedDay(cell.iso)}>
              <span>{cell.day}</span>{byDate.has(cell.iso) && <i aria-hidden="true" />}
            </button>
            <div className="na-events">
              {(byDate.get(cell.iso) || []).slice(0, 2).map(appointment => <button key={appointment._id} type="button" className="na-event" onClick={() => open(appointment)} title={appointment.attendeeName || 'Cita notarial'}>
                <strong>{timeLabel(appointment.startsAt)}</strong><span>{appointment.attendeeName || 'Firma notarial'}</span>
              </button>)}
              {(byDate.get(cell.iso) || []).length > 2 && <button className="na-more" type="button" onClick={() => setSelectedDay(cell.iso)}>+{byDate.get(cell.iso).length - 2} más</button>}
            </div>
          </div>
        ))}
      </div>
    </div>
    <div className="na-foot-grid">
      <section className="na-agenda-panel">
        <div className="na-panel-heading"><h3>{selectedDay ? `Citas del ${new Date(selectedDay + 'T12:00:00Z').toLocaleDateString('es-MX', { timeZone: 'UTC', day: 'numeric', month: 'long' })}` : 'Próximas citas del mes'}</h3>
          {selectedDay && <button type="button" onClick={() => setSelectedDay(null)}>Ver todas</button>}
        </div>
        {(selectedDay ? selected : appointments).length === 0 ? <p className="na-empty">{selectedDay ? 'No hay firmas agendadas para este día.' : 'Todavía no hay firmas programadas para este mes.'}</p> :
          <ul className="na-appointment-list">{(selectedDay ? selected : appointments).slice(0, 8).map(item => <li key={item._id}>
            <div className="na-appointment-date"><strong>{new Date(item.startsAt).toLocaleDateString('es-MX', { timeZone: TIME_ZONE, day: '2-digit' })}</strong><small>{new Date(item.startsAt).toLocaleDateString('es-MX', { timeZone: TIME_ZONE, month: 'short' })}</small></div>
            <div className="na-appointment-meta"><strong>{item.attendeeName || 'Firma notarial'}</strong><span>{timeLabel(item.startsAt)} · {item.location || 'Lugar por confirmar'}</span></div>
            <button type="button" className="na-open-case" onClick={() => open(item)}>Ver expediente</button>
          </li>)}</ul>}
      </section>
    </div>
  </section>;
}
